// src/screens/CommandCenterScreen.js
// ─────────────────────────────────────────────────────────────
// Command Center is the default post-login landing page. It is a lightweight
// "hub": it reuses existing fetches and components, shows the operations
// overview (same analytics as the Dispatch screen), a row of summary tiles
// with deep links, and an Upcoming Orders panel with rich filtering so
// dispatchers can see exactly what needs attention today / over the next N
// days / in a custom range. Nothing here duplicates backend state; every
// card deep-links into its dedicated screen for full workflow.
//
// Order rows open the SAME DispatchOrderDrawer used elsewhere (now a popup
// modal) so there is no divergence in behaviour.

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  FiSearch, FiX, FiChevronRight, FiInbox, FiTruck, FiDollarSign,
  FiLayers, FiRefreshCw, FiCalendar, FiUser, FiPackage,
  FiMapPin, FiClock, FiAlertCircle,
} from 'react-icons/fi';
import request from '../utils/request';
import {
  DispatchOpsStats,
  OpsWindowSelector,
  isTerminalOrderStatus,
  isOrderWithinNextDaysPST,
  getPSTDateString,
  getOrderDateInPST,
  opsWindowToParam,
} from '../components/commandCenter/opsStats';
import PageLayout from '../components/layout/PageLayout';
import { fetchInquiries } from '../store/thunks/inquiriesActions';
import { useResolvedOrderCoords } from '../utils/resolveOrderCoords';
import { isFullAdmin } from '../utils/adminIds';
import { needsDispatchAttention } from '../utils/dispatchOrderBuckets';
import DispatchOrderDrawer from '../components/DispatchOrderDrawer';
import RefundOpsQueue from '../components/RefundOpsQueue';
import NotificationSidebar from '../components/notifications/NotificationSidebar';
import { StatePill } from '../utils/statePill';
import { formatDate } from '../utils/formatDate';

// ─────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────

const MILES_PER_KM = 0.621371;
const PROX_THRESHOLD_KM = 16;
const DRIVER_SPARSE_THRESHOLD = 3;

function haversineKmFast(a, b) {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  const toRad = d => (d * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Filter options for the upcoming-orders panel.
const DATE_FILTER_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: '3', label: 'Next 3d' },
  { value: '7', label: 'Next 7d' },
  { value: '14', label: 'Next 14d' },
  { value: 'custom', label: 'Custom' },
  { value: 'all', label: 'All active' },
];

const STATUS_FILTER_OPTIONS = [
  { value: 'requested', label: 'Requested' },
  { value: 'pending', label: 'Pending' },
  { value: 'picked', label: 'Picked' },
  { value: 'paymentCompleted', label: 'Payment completed' },
  { value: 'out_for_delivery', label: 'Out for delivery' },
  { value: 'dispatched', label: 'Dispatched' },
];

const SORT_OPTIONS = [
  { value: 'delivery-asc', label: 'Delivery ASC' },
  { value: 'delivery-desc', label: 'Delivery DESC' },
  { value: 'updated-desc', label: 'Recently updated' },
  { value: 'status', label: 'Status' },
];

function statusRank(status) {
  const order = ['requested', 'pending', 'picked', 'paymentCompleted', 'out_for_delivery', 'dispatched', 'completed'];
  const idx = order.indexOf(status);
  return idx === -1 ? 99 : idx;
}

function lastActivityMs(order) {
  return new Date(order?.updatedAt || order?.createdAt || order?.inStockDate || 0).getTime() || 0;
}

function formatShortAddress(order) {
  const p = order?.property?.address;
  if (!p) return '—';
  const parts = [p.city, p.state].filter(Boolean).join(', ');
  return parts || (p.line1 || '—');
}

function guestEmail(order) {
  return order?.user?.email || order?.guestEmail || order?.orderInfo?.email || '';
}

function driverName(order, driverIndex) {
  const id = order?.driverID;
  if (!id) return null;
  const d = driverIndex[id] || driverIndex[String(id)];
  if (!d) return 'Assigned';
  const name = [d.firstName, d.lastName].filter(Boolean).join(' ').trim();
  return name || d.email || 'Assigned';
}

// ─────────────────────────────────────────────────────────────
// Summary tile with click-through
// ─────────────────────────────────────────────────────────────

function SummaryTile({ icon: Icon, label, count, sub, onClick, tone = 'gray', cta = 'Open' }) {
  const toneMap = {
    gray: 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white',
    indigo: 'bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800/60 text-indigo-900 dark:text-indigo-100',
    emerald: 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800/60 text-emerald-900 dark:text-emerald-100',
    amber: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/60 text-amber-900 dark:text-amber-100',
    sky: 'bg-sky-50 dark:bg-sky-900/20 border-sky-200 dark:border-sky-800/60 text-sky-900 dark:text-sky-100',
    violet: 'bg-violet-50 dark:bg-violet-900/20 border-violet-200 dark:border-violet-800/60 text-violet-900 dark:text-violet-100',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group flex flex-col items-start gap-2 rounded-2xl border px-4 py-3 text-left transition-all hover:shadow-md ${toneMap[tone] || toneMap.gray}`}
    >
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
          <span className="text-[11px] font-semibold uppercase tracking-wider opacity-70">{label}</span>
        </div>
        <FiChevronRight className="h-4 w-4 opacity-50 group-hover:translate-x-0.5 transition-transform" />
      </div>
      <div className="text-2xl font-bold leading-none">{count ?? '—'}</div>
      {sub ? <div className="text-[11px] opacity-70">{sub}</div> : null}
      <div className="mt-1 text-[11px] font-semibold opacity-80 underline underline-offset-2">
        {cta}
      </div>
    </button>
  );
}

// ─────────────────────────────────────────────────────────────
// Upcoming Orders panel
// ─────────────────────────────────────────────────────────────

function UpcomingOrdersPanel({
  orders,
  drivers,
  windowDays,
  onOpenOrder,
  onViewOnDispatch,
}) {
  // Local filter state — scoped to this panel.
  const [dateFilter, setDateFilter] = useState('auto'); // 'auto' inherits window, else overrides
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [statusSet, setStatusSet] = useState(() => new Set());
  const [driverFilter, setDriverFilter] = useState('all'); // all | assigned | unassigned | <id>
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('delivery-asc');
  const [showAttentionOnly, setShowAttentionOnly] = useState(false);

  // Resolve the effective date range.
  const effectiveRange = useMemo(() => {
    const today = new Date();
    const todayStr = getPSTDateString(today);
    if (dateFilter === 'auto') {
      if (!Number.isFinite(windowDays) || windowDays <= 0) return { from: todayStr, to: null };
      const to = new Date();
      to.setDate(to.getDate() + (windowDays - 1));
      return { from: todayStr, to: getPSTDateString(to) };
    }
    if (dateFilter === 'today') return { from: todayStr, to: todayStr };
    if (dateFilter === 'tomorrow') {
      const t = new Date(); t.setDate(t.getDate() + 1);
      return { from: getPSTDateString(t), to: getPSTDateString(t) };
    }
    if (dateFilter === 'all') return { from: null, to: null };
    if (dateFilter === 'custom') {
      return { from: customFrom || null, to: customTo || null };
    }
    const days = Number(dateFilter);
    if (Number.isFinite(days) && days > 0) {
      const to = new Date();
      to.setDate(to.getDate() + (days - 1));
      return { from: todayStr, to: getPSTDateString(to) };
    }
    return { from: null, to: null };
  }, [dateFilter, customFrom, customTo, windowDays]);

  const driverIndex = useMemo(() => {
    const map = {};
    (drivers || []).forEach(d => { if (d) map[d._id || d.id] = d; });
    return map;
  }, [drivers]);

  const toggleStatus = useCallback((value) => {
    setStatusSet(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  }, []);

  const clearAll = () => {
    setDateFilter('auto');
    setStatusSet(new Set());
    setDriverFilter('all');
    setSearch('');
    setSortBy('delivery-asc');
    setShowAttentionOnly(false);
    setCustomFrom('');
    setCustomTo('');
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = (orders || []).filter(o => !isTerminalOrderStatus(o?.status));

    // Date range
    if (effectiveRange.from || effectiveRange.to) {
      rows = rows.filter(o => {
        const d = getOrderDateInPST(o?.inStockDate);
        if (!d) return false;
        if (effectiveRange.from && d < effectiveRange.from) return false;
        if (effectiveRange.to && d > effectiveRange.to) return false;
        return true;
      });
    }

    // Status
    if (statusSet.size > 0) {
      rows = rows.filter(o => statusSet.has(o?.status));
    }

    // Driver
    if (driverFilter === 'assigned') rows = rows.filter(o => !!o?.driverID);
    else if (driverFilter === 'unassigned') rows = rows.filter(o => !o?.driverID);
    else if (driverFilter !== 'all') rows = rows.filter(o => String(o?.driverID) === String(driverFilter));

    // Attention only
    if (showAttentionOnly) {
      rows = rows.filter(o => (
        needsDispatchAttention(o) || !o?.driverID ||
        (!o?.storeOrderId && !o?.storeOrderConfirmationUrl && !(Number(o?.storeSpend) > 0))
      ));
    }

    // Search
    if (q) {
      rows = rows.filter(o => {
        const uid = String(o?.uniqueId || '').toLowerCase();
        const email = guestEmail(o).toLowerCase();
        const addr = [o?.property?.address?.line1, o?.property?.address?.city, o?.property?.address?.state].filter(Boolean).join(' ').toLowerCase();
        const dn = (driverName(o, driverIndex) || '').toLowerCase();
        return uid.includes(q) || email.includes(q) || addr.includes(q) || dn.includes(q);
      });
    }

    // Sort
    const sorted = [...rows];
    if (sortBy === 'delivery-asc') {
      sorted.sort((a, b) => new Date(a.inStockDate || 0) - new Date(b.inStockDate || 0));
    } else if (sortBy === 'delivery-desc') {
      sorted.sort((a, b) => new Date(b.inStockDate || 0) - new Date(a.inStockDate || 0));
    } else if (sortBy === 'updated-desc') {
      sorted.sort((a, b) => lastActivityMs(b) - lastActivityMs(a));
    } else if (sortBy === 'status') {
      sorted.sort((a, b) => statusRank(a.status) - statusRank(b.status));
    }
    return sorted;
  }, [orders, effectiveRange, statusSet, driverFilter, showAttentionOnly, search, sortBy, driverIndex]);

  const anyFilterActive = (
    dateFilter !== 'auto'
    || statusSet.size > 0
    || driverFilter !== 'all'
    || search.trim()
    || sortBy !== 'delivery-asc'
    || showAttentionOnly
  );

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
      {/* Panel header */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        <div>
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Upcoming Orders</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {filtered.length} shown · filter by date, status, driver, or search.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {anyFilterActive ? (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <FiX className="h-3.5 w-3.5" /> Clear filters
            </button>
          ) : null}
        </div>
      </div>

      {/* Filter controls */}
      <div className="space-y-3 border-b border-gray-200 px-5 py-4 dark:border-gray-800">
        {/* Row 1: date */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Date</span>
          <div className="inline-flex items-center gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800">
            <button
              key="auto"
              type="button"
              onClick={() => setDateFilter('auto')}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg ${dateFilter === 'auto' ? 'bg-white shadow-sm text-gray-900 dark:bg-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
            >
              Match window
            </button>
            {DATE_FILTER_OPTIONS.map(opt => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setDateFilter(opt.value)}
                className={`px-2.5 py-1 text-[11px] font-semibold rounded-lg ${dateFilter === opt.value ? 'bg-white shadow-sm text-gray-900 dark:bg-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white'}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {dateFilter === 'custom' ? (
            <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-2 py-1 dark:border-gray-700 dark:bg-gray-800"
              />
              <span>→</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-2 py-1 dark:border-gray-700 dark:bg-gray-800"
              />
            </div>
          ) : null}
        </div>

        {/* Row 2: status chips */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</span>
          {STATUS_FILTER_OPTIONS.map(opt => {
            const active = statusSet.has(opt.value);
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => toggleStatus(opt.value)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 transition-colors ${active ? 'bg-gray-900 text-white ring-gray-900 dark:bg-white dark:text-gray-900 dark:ring-white' : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-700'}`}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Row 3: driver + attention + search + sort */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Driver</span>
            <select
              value={driverFilter}
              onChange={e => setDriverFilter(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
            >
              <option value="all">All</option>
              <option value="assigned">Assigned</option>
              <option value="unassigned">Unassigned</option>
              <optgroup label="Specific">
                {(drivers || []).map(d => (
                  <option key={d._id || d.id} value={d._id || d.id}>
                    {[d.firstName, d.lastName].filter(Boolean).join(' ') || d.email || d._id}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showAttentionOnly}
              onChange={e => setShowAttentionOnly(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-500"
            />
            Needs attention only
          </label>

          <div className="relative flex-1 min-w-[180px]">
            <FiSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search id, guest, address, driver…"
              className="w-full rounded-xl border border-gray-300 bg-white pl-9 pr-3 py-2 text-sm placeholder-gray-400 focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Sort</span>
            <select
              value={sortBy}
              onChange={e => setSortBy(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
            >
              {SORT_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Rows */}
      <div className="divide-y divide-gray-100 dark:divide-gray-800">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-10 text-sm text-gray-500 dark:text-gray-400">
            <FiPackage className="h-5 w-5 opacity-60" />
            No orders match your filters.
          </div>
        ) : filtered.map(order => {
          const isUnassigned = !order.driverID;
          const needsAttention = needsDispatchAttention(order);
          return (
            <div key={order._id} className="flex flex-wrap items-center gap-3 px-5 py-3 hover:bg-gray-50 dark:hover:bg-gray-800/50">
              <div className="flex min-w-[140px] items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenOrder(order._id)}
                  className="font-mono text-sm font-semibold text-sky-700 hover:underline dark:text-sky-300"
                  title="Open order"
                >
                  #{order.uniqueId || String(order._id).slice(-5)}
                </button>
                <StatePill state={order.status} />
              </div>

              <div className="flex min-w-[140px] items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                <FiCalendar className="h-3.5 w-3.5 opacity-60" />
                {order.inStockDate ? formatDate(order.inStockDate) : '—'}
              </div>

              <div className="flex min-w-[160px] items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                <FiMapPin className="h-3.5 w-3.5 opacity-60" />
                <span className="truncate max-w-[220px]">{formatShortAddress(order)}</span>
              </div>

              <div className="flex min-w-[180px] items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                <FiUser className="h-3.5 w-3.5 opacity-60" />
                <span className="truncate max-w-[220px]">{guestEmail(order) || '—'}</span>
              </div>

              <div className="flex min-w-[160px] items-center gap-1 text-xs text-gray-700 dark:text-gray-300">
                <FiTruck className={`h-3.5 w-3.5 ${isUnassigned ? 'text-amber-500' : 'opacity-60'}`} />
                <span className="truncate">{driverName(order, driverIndex) || 'Unassigned'}</span>
              </div>

              {needsAttention ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700 ring-1 ring-amber-200 dark:bg-amber-900/20 dark:text-amber-200 dark:ring-amber-900/40">
                  <FiAlertCircle className="h-3 w-3" /> Attention
                </span>
              ) : null}

              <div className="ml-auto flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => onOpenOrder(order._id)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                >
                  Open
                </button>
                <button
                  type="button"
                  onClick={() => onViewOnDispatch(order)}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                  title="View on Dispatch"
                >
                  Dispatch
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main screen
// ─────────────────────────────────────────────────────────────

export default function CommandCenterScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { adminInfo } = useSelector(s => s.login);
  const inquiryList = useSelector(s => s.inquiries?.list || []);

  const [orders, setOrders] = useState([]);
  const [completedOrders, setCompletedOrders] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchErrors, setFetchErrors] = useState({});

  // Window for the ops stats analytics row + default for the upcoming panel.
  const [opsWindowDays, setOpsWindowDays] = useState(3);
  const [quickFilter, setQuickFilter] = useState(null);

  // Which order is currently open in the shared order modal.
  const [expandedOrderId, setExpandedOrderId] = useState(null);

  // Notification sidebar — open by default on Command Center
  const [showNotifSidebar, setShowNotifSidebar] = useState(true);

  // Pulse the refund queue when the Pending Refunds tile is clicked so the
  // operator's eye is pulled there.
  const refundQueueRef = useRef(null);
  const [refundQueuePulse, setRefundQueuePulse] = useState(false);
  const scrollToRefundQueue = useCallback(() => {
    if (refundQueueRef.current) {
      refundQueueRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setRefundQueuePulse(true);
      window.setTimeout(() => setRefundQueuePulse(false), 1800);
    }
  }, []);

  // ─── Data fetches ─────────────────────────────────────────
  // Paginated current-orders fetch that matches DispatchScreen's behavior so
  // we don't miss orders when the backend caps per-request size.
  const fetchCurrent = useCallback(async () => {
    try {
      let all = [];
      let skip = 0;
      const limit = 200;
      let hasMore = true;
      while (hasMore) {
        const params = { status: 'pending,requested,picked,paymentCompleted', limit };
        if (skip > 0) params.skip = skip;
        const { data: batch = [] } = await request.get('/orders', { params });
        if (!Array.isArray(batch) || batch.length === 0) break;
        all = all.concat(batch);
        if (batch.length < limit) hasMore = false; else skip += limit;
        if (all.length >= 5000) break;
      }
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - 7);
      const recent = all.filter(o => !o.inStockDate || new Date(o.inStockDate) >= cutoff);
      setOrders(recent.sort((a, b) => new Date(a.inStockDate || 0) - new Date(b.inStockDate || 0)));
      setFetchErrors(e => ({ ...e, orders: null }));
    } catch (e) {
      console.error('fetchCurrent', e);
      setFetchErrors(err => ({ ...err, orders: e?.response?.data?.message || e?.message || 'Failed to load orders' }));
    }
  }, []);

  const fetchCompleted = useCallback(async () => {
    try {
      const params = { page: 1, limit: 200, status: 'completed', sort: 'inStockDate', order: 'desc' };
      const { data = [] } = await request.get('/orders/completed', { params });
      setCompletedOrders(Array.isArray(data) ? data : []);
      setFetchErrors(e => ({ ...e, completed: null }));
    } catch (e) {
      console.error('fetchCompleted', e);
      setFetchErrors(err => ({ ...err, completed: e?.response?.data?.message || e?.message || 'Failed' }));
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    try {
      const res = await request.get('/drivers');
      setDrivers(Array.isArray(res?.data) ? res.data : []);
      setFetchErrors(e => ({ ...e, drivers: null }));
    } catch (e) {
      console.error('drivers', e);
      setFetchErrors(err => ({ ...err, drivers: e?.response?.data?.message || e?.message || 'Failed' }));
    }
  }, []);

  const fetchHosts = useCallback(async () => {
    try {
      const res = await request.get('/hosts');
      const all = Array.isArray(res?.data) ? res.data : [];
      setHosts(all);
      setFetchErrors(e => ({ ...e, hosts: null }));
    } catch (e) {
      console.error('hosts', e);
      setFetchErrors(err => ({ ...err, hosts: e?.response?.data?.message || e?.message || 'Failed' }));
    }
  }, []);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([fetchCurrent(), fetchCompleted(), fetchDrivers(), fetchHosts()]);
      dispatch(fetchInquiries());
    } finally {
      setRefreshing(false);
    }
  }, [dispatch, fetchCurrent, fetchCompleted, fetchDrivers, fetchHosts]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        await refresh();
      } finally {
        setLoading(false);
      }
    })();
  }, [refresh]);

  useEffect(() => {
    dispatch(fetchInquiries());
    const id = setInterval(() => dispatch(fetchInquiries()), 60_000);
    return () => clearInterval(id);
  }, [dispatch]);

  // ─── Derived analytics ────────────────────────────────────
  const inquiriesCount = useMemo(() => {
    const todayStr = getPSTDateString();
    let open = 0, awaiting = 0, today = 0;
    for (const i of inquiryList) {
      if (i.closed) continue;
      open += 1;
      if (!i.replied) awaiting += 1;
      if (i.createdAt && getPSTDateString(new Date(i.createdAt)) === todayStr) today += 1;
    }
    return { open, awaiting, today };
  }, [inquiryList]);

  const pendingForScarcity = useMemo(() => {
    const excluded = new Set(['completed', 'created', 'pending']);
    return (orders || []).filter(o => {
      if (!o) return false;
      if (isTerminalOrderStatus(o.status)) return false;
      if (excluded.has(o.status)) return false;
      if (!isOrderWithinNextDaysPST(o, opsWindowDays)) return false;
      return true;
    });
  }, [orders, opsWindowDays]);

  const resolvedOrderCoords = useResolvedOrderCoords(pendingForScarcity);

  const driversWithLatLng = useMemo(() => (
    (drivers || []).map(d => {
      const lat = Number(d?.currentAddress?.lat);
      const lng = Number(d?.currentAddress?.lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
      const rangeMi = Number(d?.deliveryRange);
      return { id: d.id || d._id, coord: { lat, lng }, rangeMi: Number.isFinite(rangeMi) && rangeMi > 0 ? rangeMi : null };
    }).filter(Boolean)
  ), [drivers]);

  const driverSparseIdSet = useMemo(() => {
    const set = new Set();
    if (driversWithLatLng.length === 0) return set;
    pendingForScarcity.forEach(order => {
      const coord = resolvedOrderCoords[order._id];
      if (!coord) return;
      let nearby = 0;
      for (const d of driversWithLatLng) {
        const km = haversineKmFast(d.coord, coord);
        const distMi = km * MILES_PER_KM;
        const maxMi = d.rangeMi != null ? d.rangeMi : PROX_THRESHOLD_KM * MILES_PER_KM;
        if (distMi <= maxMi) {
          nearby += 1;
          if (nearby >= DRIVER_SPARSE_THRESHOLD) break;
        }
      }
      if (nearby < DRIVER_SPARSE_THRESHOLD) set.add(order._id);
    });
    return set;
  }, [pendingForScarcity, resolvedOrderCoords, driversWithLatLng]);

  const driverSparseUnresolved = useMemo(() => (
    pendingForScarcity.filter(o => !resolvedOrderCoords[o._id]).length
  ), [pendingForScarcity, resolvedOrderCoords]);

  // Pending Refunds: at least one approved refund line item AND the guest
  // refund hasn't been manually issued yet. Mirrors RefundOpsQueue semantics.
  const pendingRefundsStats = useMemo(() => {
    const merged = [...orders, ...completedOrders];
    let orderCount = 0;
    let totalPending = 0;
    merged.forEach((o) => {
      if (!o || o?.guestRefund?.issued === true) return;
      let orderHasApproved = false;
      let orderPendingSum = 0;
      (o.products || []).forEach((p) => {
        if (p?.refunded === true && p?.refundApproved === true) {
          orderHasApproved = true;
          orderPendingSum += (Number(p.price) || 0) * (Number(p.quantity) || 1);
        }
      });
      if (orderHasApproved) {
        orderCount += 1;
        totalPending += orderPendingSum;
      }
    });
    return { orderCount, totalPending };
  }, [orders, completedOrders]);

  // ─── Handlers ─────────────────────────────────────────────
  // Deep-link into Dispatch with a pre-applied quickFilter + window.
  const openDispatchWith = useCallback((filterValue) => {
    const days = opsWindowToParam(opsWindowDays);
    const params = new URLSearchParams();
    if (filterValue) params.set('filter', filterValue);
    if (days) params.set('days', days);
    navigate(`/dispatch${params.toString() ? `?${params.toString()}` : ''}`);
  }, [navigate, opsWindowDays]);

  const handleOpenStat = useCallback((key) => {
    setQuickFilter(prev => prev === key ? null : key);
    openDispatchWith(key);
  }, [openDispatchWith]);

  const handleOpenOrder = useCallback((orderId) => {
    setExpandedOrderId(orderId);
  }, []);

  const handleCloseOrder = useCallback(() => {
    setExpandedOrderId(null);
  }, []);

  const handleRefreshOrder = useCallback(async () => {
    try { await fetchCurrent(); } catch (e) { /* noop */ }
  }, [fetchCurrent]);

  const patchOrder = useCallback(async (id, data) => {
    try {
      await request.patch(`/orders/${id}/dispatch`, data);
      setExpandedOrderId(null);
      await fetchCurrent();
    } catch (e) {
      console.error('patchOrder failed', e);
      // Surface to the drawer so it can show the backend error and avoid
      // an optimistic local merge that lies about the persisted state.
      throw e;
    }
  }, [fetchCurrent]);

  const expandedOrder = useMemo(() => {
    if (!expandedOrderId) return null;
    return orders.find(o => o._id === expandedOrderId)
      || completedOrders.find(o => o._id === expandedOrderId)
      || null;
  }, [expandedOrderId, orders, completedOrders]);

  const properties = useMemo(() => {
    const set = new Map();
    [...orders, ...completedOrders].forEach(o => {
      if (o?.property?._id) set.set(o.property._id, o.property);
    });
    return Array.from(set.values());
  }, [orders, completedOrders]);

  // ─── Render ───────────────────────────────────────────────
  const headerActions = (
    <div className="flex items-center gap-2">
      <OpsWindowSelector value={opsWindowDays} onChange={setOpsWindowDays} />
      <button
        type="button"
        onClick={() => setShowNotifSidebar(prev => !prev)}
        className={`inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-xs font-semibold shadow-sm transition-all ${showNotifSidebar ? 'bg-red-500 text-white border-red-500' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'}`}
      >
        <FiAlertCircle className="h-3.5 w-3.5" />
        Alerts
      </button>
      <button
        type="button"
        onClick={refresh}
        disabled={refreshing}
        className="inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-60 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
      >
        <FiRefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
        {refreshing ? 'Refreshing…' : 'Refresh'}
      </button>
    </div>
  );

  return (
    <PageLayout
      title="Command Center"
      subtitle="Operations overview · upcoming orders · refund queue"
      showSearch={false}
      fullWidth
      noPadding
      actions={headerActions}
    >
      <div className="mx-auto max-w-[1800px] px-6 py-6 space-y-6">
        {/* Data-status banner — shows what's loaded so zero-analytics don't
            look like a silent failure. */}
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs dark:border-gray-800 dark:bg-gray-900">
          <span className="inline-flex items-center gap-1 font-semibold text-gray-700 dark:text-gray-300">
            <FiLayers className="h-3.5 w-3.5 opacity-70" /> Loaded
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Orders: <strong>{orders.length}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Completed: <strong>{completedOrders.length}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Drivers: <strong>{drivers.length}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Inquiries: <strong>{inquiryList.length}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-400">
            Hosts: <strong>{hosts.length}</strong>
          </span>
          {Object.entries(fetchErrors).filter(([, v]) => v).length > 0 ? (
            <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700 ring-1 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-200">
              <FiAlertCircle className="h-3 w-3" />
              Fetch errors: {Object.entries(fetchErrors).filter(([, v]) => v).map(([k]) => k).join(', ')}
            </span>
          ) : null}
        </div>

        {/* Ops stats row */}
        <DispatchOpsStats
          orders={orders}
          quickFilter={quickFilter}
          setQuickFilter={handleOpenStat}
          inquiriesCount={inquiriesCount}
          onOpenInquiries={() => navigate('/inquiries')}
          driverSparseCount={driverSparseIdSet.size}
          driverSparseSub={
            driverSparseUnresolved > 0
              ? `Low driver coverage · ${driverSparseUnresolved} unresolved`
              : 'Low driver coverage near delivery area'
          }
          windowDays={opsWindowDays}
          onChangeWindowDays={setOpsWindowDays}
          showWindowSelector={false}
          title="Operations overview"
        />

        {/* Summary tiles */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryTile
            icon={FiDollarSign}
            label="Pending Refunds"
            count={pendingRefundsStats.orderCount}
            sub={
              pendingRefundsStats.orderCount > 0
                ? `${new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(pendingRefundsStats.totalPending)} total pending · ${pendingRefundsStats.orderCount} order${pendingRefundsStats.orderCount === 1 ? '' : 's'}`
                : 'No refunds awaiting manual payout'
            }
            tone="violet"
            cta="Jump to queue"
            onClick={scrollToRefundQueue}
          />
          <SummaryTile
            icon={FiInbox}
            label="Inquiries"
            count={inquiriesCount.open}
            sub={`${inquiriesCount.awaiting} awaiting reply`}
            tone="indigo"
            onClick={() => navigate('/inquiries')}
          />
          <SummaryTile
            icon={FiTruck}
            label="Dispatch"
            count={orders.filter(o => !isTerminalOrderStatus(o.status)).length}
            sub="Active orders across tabs"
            tone="emerald"
            onClick={() => openDispatchWith(null)}
          />
          <SummaryTile
            icon={FiClock}
            label="Live Orders"
            count={orders.filter(o => o.status === 'out_for_delivery' || o.status === 'dispatched').length}
            sub="Currently in motion"
            tone="amber"
            onClick={() => navigate('/orders/live')}
          />
        </div>

        {/* Refund Operations — always visible, directly under Ops stats so
            staff can see pending payouts without scrolling past orders. */}
        {isFullAdmin(adminInfo) ? (
          <div
            ref={refundQueueRef}
            className={`scroll-mt-24 rounded-2xl border bg-amber-50/40 p-4 transition-shadow duration-300 dark:bg-amber-900/10 ${
              refundQueuePulse
                ? 'border-amber-400 shadow-[0_0_0_4px_rgba(251,191,36,0.25)]'
                : 'border-amber-200 dark:border-amber-900/40'
            }`}
          >
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-200">
              <FiDollarSign className="h-4 w-4" />
              Refund Operations (Admin)
              {pendingRefundsStats.orderCount > 0 ? (
                <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-200/70 px-2 py-0.5 text-[11px] font-semibold text-amber-900 dark:bg-amber-400/20 dark:text-amber-100">
                  {pendingRefundsStats.orderCount} pending · {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(pendingRefundsStats.totalPending)}
                </span>
              ) : null}
            </div>
            <RefundOpsQueue
              orders={orders}
              completedOrders={completedOrders}
              hosts={hosts}
              onOpenOrder={(id) => handleOpenOrder(id)}
              onOrderUpdated={(updated) => {
                if (!updated?._id) return;
                setOrders(os => os.map(o => o._id === updated._id ? { ...o, ...updated } : o));
                setCompletedOrders(os => os.map(o => o._id === updated._id ? { ...o, ...updated } : o));
              }}
            />
          </div>
        ) : null}

        {/* Upcoming Orders */}
        {loading ? (
          <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white py-16 shadow-sm dark:border-gray-800 dark:bg-gray-900">
            <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-900" />
              Loading orders…
            </div>
          </div>
        ) : (
          <UpcomingOrdersPanel
            orders={orders}
            drivers={drivers}
            windowDays={opsWindowDays}
            onOpenOrder={handleOpenOrder}
            onViewOnDispatch={(order) => {
              const days = opsWindowToParam(opsWindowDays);
              navigate(`/dispatch?orderId=${order._id}${days ? `&days=${days}` : ''}`);
            }}
          />
        )}

      </div>

      {/* Shared order modal (DispatchOrderDrawer now renders as a centered popup) */}
      {expandedOrder ? (
        <DispatchOrderDrawer
          order={expandedOrder}
          isOpen={!!expandedOrderId}
          closeDrawer={handleCloseOrder}
          drivers={drivers}
          properties={properties}
          patchOrder={patchOrder}
          refreshOrders={handleRefreshOrder}
          completedOrders={completedOrders}
        />
      ) : null}

      {/* Notification Sidebar */}
      <NotificationSidebar
        isOpen={showNotifSidebar}
        onClose={() => setShowNotifSidebar(false)}
        onNavigateToOrder={(id) => {
          handleOpenOrder(id);
          setShowNotifSidebar(false);
        }}
        onNavigateToInquiry={() => navigate('/inquiries')}
        onNavigateToServiceRequest={() => navigate('/service-requests')}
      />
    </PageLayout>
  );
}
